/**
 * GuestTripPage — iter197.
 *
 * Public route at `/guest/trip/:token`. Read-only trip summary for someone
 * who joined the trip but isn't on the platform. Default TTL: 30 days.
 *
 * Shows totals, category breakdown, expense list. NO settlements, balances,
 * or member-internal data — strictly the "what we spent, who paid for what"
 * audit trail.
 */
import { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Calendar, Users, Coins, Loader2, ShieldAlert, Sparkles, MapPin, Receipt,
} from 'lucide-react';
import { Button } from '../components/ui/button';

const BASE = process.env.REACT_APP_BACKEND_URL;
const publicAxios = axios.create({ baseURL: `${BASE}/api` });

const SYMS = {
  INR: '₹', USD: '$', EUR: '€', GBP: '£', SGD: 'S$', AED: 'د.إ',
  AUD: 'A$', CAD: 'C$', JPY: '¥',
};

function sym(c) { return SYMS[c] || '₹'; }

function fmtAmt(amount, currency) {
  const v = Math.round(Number(amount || 0));
  return `${sym(currency)}${v.toLocaleString()}`;
}

function fmtDate(d) {
  if (!d) return '—';
  try {
    return new Date(`${d}T00:00:00`).toLocaleDateString(undefined, {
      day: 'numeric', month: 'short', year: 'numeric',
    });
  } catch { return d; }
}

function fmtDateTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      day: 'numeric', month: 'short',
    });
  } catch { return iso; }
}

export default function GuestTripPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await publicAxios.get(`/guest/trip/${token}`);
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Link is invalid or expired');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6 text-center">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground mb-3" />
        <p className="text-sm text-muted-foreground">Loading trip…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6 text-center">
        <ShieldAlert className="w-10 h-10 text-amber-500 mb-3" />
        <h1 className="font-display text-xl font-bold mb-1">Link unavailable</h1>
        <p className="text-sm text-muted-foreground max-w-xs" data-testid="guest-trip-error">{error}</p>
        <Button onClick={() => navigate('/welcome')} variant="outline" className="mt-6">Back to home</Button>
      </div>
    );
  }

  if (!data) return null;

  const { group, summary, breakdown, members, expenses, invitation } = data;
  const currency = group.currency || 'INR';

  return (
    <div className="min-h-screen pb-24 bg-gradient-to-b from-background to-muted/30" data-testid="guest-trip-page">
      <main className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {/* Brand strip */}
        <div className="text-center">
          <p className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">
            <Sparkles className="w-3 h-3" /> SplitEasy · Trip view
          </p>
        </div>

        {/* Hero */}
        <header className="text-center space-y-2" data-testid="guest-trip-header">
          <h1 className="font-display text-2xl sm:text-3xl font-bold leading-tight">
            {group.name}
          </h1>
          {group.trip_start_date && (
            <p className="text-sm text-muted-foreground inline-flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5" />
              {fmtDate(group.trip_start_date)}
              {group.trip_end_date ? ` → ${fmtDate(group.trip_end_date)}` : ''}
            </p>
          )}
          {invitation?.note && (
            <p className="text-xs text-muted-foreground italic max-w-sm mx-auto">
              &ldquo;{invitation.note}&rdquo;
            </p>
          )}
        </header>

        {/* Summary tiles */}
        <section className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="guest-trip-summary">
          <Tile label="Total spent" value={fmtAmt(summary.total_spent, currency)} testId="guest-trip-total" />
          <Tile label="Expenses" value={summary.expense_count} testId="guest-trip-count" />
          <Tile label="Direct paid" value={fmtAmt(summary.direct_total, currency)} testId="guest-trip-direct" />
          <Tile label="Members" value={summary.member_count} testId="guest-trip-members" />
        </section>

        {/* Category breakdown */}
        {breakdown.length > 0 && (
          <section className="rounded-2xl border-2 border-border bg-card p-5 space-y-3" data-testid="guest-trip-categories">
            <header className="flex items-center gap-2">
              <Coins className="w-4 h-4 text-primary" />
              <h2 className="font-display text-sm font-bold uppercase tracking-[0.16em]">Where the money went</h2>
            </header>
            <div className="space-y-2">
              {breakdown.slice(0, 8).map((c) => (
                <div key={c.category} className="space-y-1" data-testid={`guest-trip-category-${c.category}`}>
                  <div className="flex items-center justify-between text-xs font-semibold">
                    <span className="capitalize">{c.category.replace(/_/g, ' ')}</span>
                    <span className="tabular-nums">
                      {fmtAmt(c.amount, currency)}
                      <span className="text-muted-foreground font-normal ml-1.5">({c.pct}%)</span>
                    </span>
                  </div>
                  <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                    <div className="h-full bg-primary transition-all" style={{ width: `${Math.min(100, c.pct)}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Member list */}
        <section className="rounded-2xl border-2 border-border bg-card p-5 space-y-2" data-testid="guest-trip-people">
          <header className="flex items-center gap-2 mb-2">
            <Users className="w-4 h-4 text-primary" />
            <h2 className="font-display text-sm font-bold uppercase tracking-[0.16em]">Who came</h2>
          </header>
          <div className="flex flex-wrap gap-1.5">
            {members.map((m) => (
              <span
                key={m.id}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-muted/40 border border-border text-xs font-semibold"
                data-testid={`guest-trip-member-${m.id}`}
              >
                {m.name}
              </span>
            ))}
          </div>
        </section>

        {/* Expense list */}
        <section className="rounded-2xl border-2 border-border bg-card overflow-hidden" data-testid="guest-trip-expenses">
          <header className="px-5 py-3 border-b border-border flex items-center gap-2 bg-muted/30">
            <Receipt className="w-4 h-4 text-primary" />
            <h2 className="font-display text-sm font-bold uppercase tracking-[0.16em]">All expenses</h2>
            <span className="text-xs text-muted-foreground ml-auto">{expenses.length} row(s)</span>
          </header>
          {expenses.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-muted-foreground">No expenses yet.</p>
          ) : (
            <div className="divide-y divide-border">
              {expenses.slice(0, 100).map((e) => (
                <div key={e.id} className="px-5 py-3 flex items-start gap-3 text-sm" data-testid={`guest-trip-expense-${e.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold truncate">{e.description}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      {e.category ? <span className="capitalize">{e.category.replace(/_/g, ' ')}</span> : ''}
                      {e.paid_by_name ? ` · paid by ${e.paid_by_name}` : ''}
                      {e.direct_payment ? ' · direct' : ''}
                      {e.approved_at ? ` · ${fmtDateTime(e.approved_at)}` : ''}
                    </p>
                  </div>
                  <span className="text-sm font-bold tabular-nums shrink-0">
                    {e.entry_currency && e.entry_currency !== currency
                      ? <>{fmtAmt(e.entry_amount, e.entry_currency)} <span className="text-xs font-normal text-muted-foreground">({fmtAmt(e.amount, currency)})</span></>
                      : fmtAmt(e.amount, currency)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </section>

        <p className="text-xs text-center text-muted-foreground pt-3" data-testid="guest-trip-expiry">
          This view expires {new Date(data.expires_at).toLocaleString()}.
        </p>

        <div className="text-center">
          <a href="/welcome" className="text-xs text-muted-foreground hover:text-foreground underline-offset-2 hover:underline">
            Powered by SplitEasy
          </a>
        </div>
      </main>
    </div>
  );
}

function Tile({ label, value, testId }) {
  return (
    <div className="rounded-xl border-2 border-border bg-card px-3 py-2.5" data-testid={testId}>
      <p className="text-xs uppercase tracking-[0.18em] font-bold text-muted-foreground">{label}</p>
      <p className="font-display text-lg font-bold tabular-nums leading-tight mt-1 truncate">{value}</p>
    </div>
  );
}
